import React, { useContext, useState } from 'react';

import AJAX from 'common/AJAX';
import { ShowToastContext } from 'common/containers/ToastContainer';
import SourceFilter from 'common/subdomain/admin/AdminQueue/SourceFilters/SourceFilter';
import ButtonV2 from 'common/ui/ButtonV2';
import { H2, P } from 'common/ui/Text';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import styles from 'css-module/components/subdomain/admin/AdminZendeskSettings/_GroupsFilter.module.scss';

import { useZendeskSourceFilter } from '../AdminQueue/SourceFilters/useZendeskSourceFilter';

import type { Company } from 'common/containers/CompanyContainer';

export default function GroupsFilter({ zendesk }: { zendesk: NonNullable<Company['zendesk']> }) {
  const showToast = useContext(ShowToastContext);

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedGroupIDs, setSelectedGroupIDs] = useState<string[] | null>(
    zendesk?.groupIDs ?? null,
  );

  const { loading, error, options, selectedOptions } = useZendeskSourceFilter(selectedGroupIDs);

  const handleChange = (teamInboxIDs: string[] | null) => {
    setHasChanges(true);
    setSelectedGroupIDs(teamInboxIDs);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(false);

    const response = await AJAX.post('/api/zendesk/setGroups', {
      groupIDs: selectedGroupIDs,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    setSaving(false);

    if (error) {
      setSaveError(true);
    } else {
      showToast('Groups saved');
      setHasChanges(false);
    }
  };

  return (
    <div className={styles.groupsFilter}>
      <H2 variant="headingMd">Autopilot settings</H2>
      <P className={styles.description} variant="bodyMd">
        Use filters to specify which conversations are processed by Autopilot.
      </P>
      <div className={styles.filterContainer}>
        <SourceFilter
          loading={loading}
          error={error}
          options={options}
          selectedOptions={selectedOptions}
          filterLabel="Filter by group"
          onChange={handleChange}
          saveError={saveError}
          optionsPlacement="top"
        />
        <ButtonV2
          className={styles.saveButton}
          size="medium"
          disabled={!hasChanges}
          loading={saving}
          onClick={handleSave}>
          Save
        </ButtonV2>
      </div>
    </div>
  );
}
